import { Component, inject, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { RouterLink, ActivatedRoute } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { FormField, Input, Checkbox, ZagButton, AppStatusComponent } from '@zag/ui';
import { AuthService } from './auth.service';
import { AuthModeService } from './auth-mode.service';
import { AUTH_SHOW_SIGNUP } from './auth-routes';
import { SocialLoginButtonsComponent } from './social-login-buttons/social-login-buttons.component';

@Component({
  selector: 'sc-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    RouterLink,
    ReactiveFormsModule,
    TranslatePipe,
    FormField,
    Input,
    Checkbox,
    ZagButton,
    SocialLoginButtonsComponent,
    AppStatusComponent,
  ],
  template: `
    <div class="auth-container">
      <div class="auth-form">
        <h2 class="auth-header">
          <img src="/logo.svg" alt="Logo" />
          <span>{{ 'auth.login' | translate }}</span>
        </h2>

        @if (authService.error()) {
          <div class="error-message">
            {{ authService.error() }}
          </div>
        }

        <!-- Configured credential/password form -->
        @if (authModeService.emailPasswordEnabled()) {
          <form [formGroup]="loginForm" (ngSubmit)="onSubmit()" method="post">
            <div class="form-fields">
              <zag-form-field
                [label]="credentialLabel() | translate"
                [error]="credentialError() | translate"
                [required]="true"
                forId="login-identifier"
              >
                <zag-input
                  id="login-identifier"
                  formControlName="identifier"
                  [type]="authModeService.usernameEnabled() ? 'text' : 'email'"
                  [placeholder]="credentialPlaceholder() | translate"
                ></zag-input>
              </zag-form-field>

              <zag-form-field
                [label]="'auth.password' | translate"
                [error]="
                  loginForm.get('password')?.touched && loginForm.get('password')?.invalid ? ('errors.passwordMinLength' | translate) : ''
                "
                [required]="true"
                forId="login-password"
              >
                <zag-input
                  id="login-password"
                  formControlName="password"
                  type="password"
                  [placeholder]="'auth.passwordPlaceholder' | translate"
                ></zag-input>
              </zag-form-field>

              <zag-form-field>
                <zag-checkbox formControlName="rememberMe" [label]="'auth.rememberMe' | translate"></zag-checkbox>
              </zag-form-field>

              <button zagButton zagType="primary" type="submit" [disabled]="authService.isLoading()" class="submit-button">
                {{ authService.isLoading() ? ('auth.signingIn' | translate) : ('auth.signIn' | translate) }}
              </button>

              @if (!authModeService.usernameEnabled()) {
                <div class="auth-forgot">
                  <a routerLink="/auth/forgot-password" class="link">{{ 'auth.forgotPassword' | translate }}</a>
                </div>
              }
            </div>
          </form>
        }

        <!-- Divider between email/password and social login -->
        @if (authModeService.emailPasswordEnabled() && authModeService.hasSocialProviders()) {
          <div class="auth-divider">
            <span>{{ 'auth.or' | translate }}</span>
          </div>
        }

        <!-- Social login buttons -->
        @if (authModeService.hasSocialProviders()) {
          <sc-social-login-buttons
            [providers]="authModeService.socialProviders()"
            [disabled]="authService.isLoading()"
            (providerClick)="signInWithSocial($event)"
          />
        }

        @if (showSignUp && authModeService.emailPasswordEnabled()) {
          <div class="auth-footer">
            <p>
              {{ 'auth.noAccount' | translate }}
              <a routerLink="/register" class="link">{{ 'auth.signUp' | translate }}</a>
            </p>
          </div>
        }
      </div>
      <footer class="auth-status-footer">
        <zag-app-status />
      </footer>
    </div>
  `,
  styles: [
    `
      .auth-container {
        position: relative;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: var(--spacing-md);
        height: 100dvh;
        box-sizing: border-box;
        overflow: hidden;
      }

      .auth-status-footer {
        position: absolute;
        bottom: var(--spacing-md);
        left: 0;
        right: 0;
        display: flex;
        justify-content: center;
        pointer-events: none;
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
        max-width: 400px;
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .auth-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
      }

      .auth-header img {
        width: 92px;
        height: auto;
      }

      .error-message {
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--color-error-bg);
        border: 1px solid var(--color-error-border);
        border-radius: var(--border-radius-sm);
        color: var(--color-error);
        font-size: var(--font-size-sm);
      }

      .social-login-section {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-sm);
      }

      .social-login-button {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--spacing-sm);
        width: 100%;
        padding: var(--spacing-sm) var(--spacing-md);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-medium);
      }

      .social-icon {
        width: 20px;
        height: 20px;
        flex-shrink: 0;
      }

      .auth-divider {
        display: flex;
        align-items: center;
        gap: var(--spacing-md);
        color: var(--color-neutral-2);
        font-size: var(--font-size-sm);
      }

      .auth-divider::before,
      .auth-divider::after {
        content: '';
        flex: 1;
        height: 1px;
        background-color: var(--border-color-default);
      }

      .auth-footer {
        text-align: center;
        margin-top: var(--spacing-md);
      }

      .auth-forgot {
        text-align: center;
      }

      .link {
        color: var(--color-primary-alt);
        text-decoration: none;
        font-weight: var(--font-weight-medium);
      }

      .link:hover {
        text-decoration: underline;
      }
    `,
  ],
})
export class LoginComponent {
  protected readonly authService = inject(AuthService);
  protected readonly authModeService = inject(AuthModeService);
  private readonly route = inject(ActivatedRoute);
  protected readonly showSignUp = inject(AUTH_SHOW_SIGNUP);

  constructor() {
    // Reload auth mode on every visit so settings changes made in the same SPA
    // session (e.g. enabling username auth) are reflected immediately.
    this.authModeService.authModeResource.reload();
  }

  readonly loginForm = new FormGroup({
    identifier: new FormControl('', [Validators.required, control => this.validateCredential(control)]),
    password: new FormControl('', [Validators.required, Validators.minLength(8)]),
    rememberMe: new FormControl(false),
  });

  /**
   * Initiate social login via the auth service.
   *
   * Posts to better-auth's social sign-in endpoint, then redirects the
   * browser to the OAuth provider's authorization page.
   *
   * The callbackURL MUST be an absolute URL so that after the OAuth
   * callback (which happens directly on the API server), better-auth
   * redirects back to the Angular dev server (different port in dev).
   */
  signInWithSocial(providerId: string): void {
    const returnUrl = this.route.snapshot.queryParams['returnUrl'] ?? '/';
    // Build an absolute URL so the post-OAuth redirect lands on the
    // frontend origin (e.g., http://localhost:4200/) rather than the
    // API server origin (e.g., http://localhost:3200/).
    const callbackURL = new URL(returnUrl, globalThis.location.origin).href;
    this.authService.signInWithSocial(providerId, callbackURL);
  }

  async onSubmit(): Promise<void> {
    this.loginForm.controls.identifier.updateValueAndValidity();
    if (this.loginForm.valid) {
      this.authService.clearError();

      const { identifier, password, rememberMe } = this.loginForm.value;

      try {
        if (!identifier || !password) {
          return;
        }

        const returnUrl = this.route.snapshot.queryParams['returnUrl'];

        await this.authService.signIn(
          this.authModeService.usernameEnabled() && Validators.email(new FormControl(identifier)) !== null
            ? { username: identifier, password, rememberMe: rememberMe ?? false }
            : { email: identifier, password, rememberMe: rememberMe ?? false },
          returnUrl
        );
      } catch {
        // Error is handled by the auth service via authService.error() signal
      }
    } else {
      this.loginForm.markAllAsTouched();
    }
  }

  protected credentialLabel(): string {
    return this.authModeService.usernameEnabled() ? 'auth.emailOrUsername' : 'auth.email';
  }

  protected credentialPlaceholder(): string {
    return this.authModeService.usernameEnabled() ? 'auth.emailOrUsernamePlaceholder' : 'auth.emailPlaceholder';
  }

  protected credentialError(): string {
    const control = this.loginForm.controls.identifier;
    if (!control.touched || control.valid) return '';
    return this.authModeService.usernameEnabled() ? 'errors.invalidEmailOrUsername' : 'errors.invalidEmail';
  }

  private validateCredential(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (typeof value !== 'string' || value.length === 0) return null;
    if (!this.authModeService.usernameEnabled() || Validators.email(control) === null) return Validators.email(control);

    const policy = this.authModeService.usernamePolicy();
    const minLength = policy?.minLength ?? 5;
    const maxLength = policy?.maxLength ?? 30;
    return /^[a-zA-Z0-9_.]+$/.test(value) && value.length >= minLength && value.length <= maxLength ? null : { username: true };
  }
}
